/**
 * Canonical SkillQuest color registry (12 families × static/light/dark/deep).
 *
 * - `static` / `light` — authored brand pairs
 * - `dark` — theme-darkened pairs (badges / fills under `.dark`)
 * - `deep` — solid deep hexes for borders / strokes (theme-stable)
 *
 * Documented out-of-palette exceptions (not duplicated elsewhere):
 * - `#334155` — learn progress ring dark-mode track (`progress-circle.tsx`)
 * - `#FF72E1` — mythic rarity accent (`rarity-tier-colors.ts` / `RARITY_TIER_MYTHIC_ACCENT_HEX`)
 * - `#46311c` — learn sidebar highlighted stop border (`learn-sidebar-structure-chrome.ts`)
 */
import { formatHexColor, hexToRgba, parseHexColor } from "./sq-color-hex";
import type { SqColorGradientPair } from "./sq-color-types";
import {
	SQ_ORANGE_REFERENCE_DELTA,
	sqDeriveThemeDarkPair,
} from "./sq-derive-theme-dark-pair";

export type { SqColorGradientPair } from "./sq-color-types";

export type SqColorName =
	| "red"
	| "blue"
	| "green"
	| "amber"
	| "gold"
	| "purple"
	| "orange"
	| "bronze"
	| "silver"
	| "slate"
	| "grey"
	| "indigo";

/** Soft translucent status chips (draft / published / archived / optional). */
export type SqSoftColorName =
	| "green_soft"
	| "orange_soft"
	| "grey_soft"
	| "indigo_soft";

export type SqColorMode = "light" | "dark" | "static" | "deep";

export interface SqColorModes {
	static: SqColorGradientPair;
	light: SqColorGradientPair;
	dark: SqColorGradientPair;
	/** Solid deep tone (from === to) for borders / strokes. */
	deep: SqColorGradientPair;
}

/** Authored static/light pairs — sole source of palette hex literals. */
export const SQ_COLOR_STATIC_PAIRS: Record<SqColorName, SqColorGradientPair> = {
	red: { from: "#C23D00", to: "#780101" },
	blue: { from: "#14ADFF", to: "#006BA4" },
	green: { from: "#A8C200", to: "#7A9300" },
	amber: { from: "#FFC01F", to: "#D99800" },
	gold: { from: "#FACC15", to: "#CA8A04" },
	purple: { from: "#B62FFF", to: "#6D1C99" },
	orange: { from: "#FFA12E", to: "#BA6103" },
	bronze: { from: "#D97706", to: "#78350F" },
	silver: { from: "#E2E8F0", to: "#64748B" },
	slate: { from: "#CBD5E1", to: "#94A3B8" },
	grey: { from: "#141924", to: "#111111" },
	indigo: { from: "#4F46E5", to: "#312E81" },
};

/**
 * Authored deep border/stroke hexes. Red/green locked for victory/defeat chrome;
 * remaining families derived from static.from (see {@link sqDeriveDeepBorderHex}).
 */
export const SQ_COLOR_DEEP_HEX_OVERRIDES: Partial<Record<SqColorName, string>> =
	{
		red: "#531500",
		green: "#2A361D",
	};

/** Factor from static.from → deep solid when no override (calibrated near green). */
export const SQ_DEEP_FROM_STATIC_FACTOR = 0.28;

export function sqDeriveDeepBorderHex(staticFrom: string): string {
	const { r, g, b } = parseHexColor(staticFrom);
	return formatHexColor(
		r * SQ_DEEP_FROM_STATIC_FACTOR,
		g * SQ_DEEP_FROM_STATIC_FACTOR,
		b * SQ_DEEP_FROM_STATIC_FACTOR,
	);
}

function deepPairForColor(name: SqColorName): SqColorGradientPair {
	const override = SQ_COLOR_DEEP_HEX_OVERRIDES[name];
	const hex =
		override ?? sqDeriveDeepBorderHex(SQ_COLOR_STATIC_PAIRS[name].from);
	return { from: hex, to: hex };
}

const SQ_COLOR_NAMES = Object.keys(SQ_COLOR_STATIC_PAIRS) as SqColorName[];

function buildSqColors(): Record<SqColorName, SqColorModes> {
	const result = {} as Record<SqColorName, SqColorModes>;
	for (const name of SQ_COLOR_NAMES) {
		const staticPair = SQ_COLOR_STATIC_PAIRS[name];
		const derivedDark = sqDeriveThemeDarkPair(
			staticPair,
			SQ_ORANGE_REFERENCE_DELTA,
		);
		result[name] = {
			static: staticPair,
			light: staticPair,
			dark: derivedDark,
			deep: deepPairForColor(name),
		};
	}
	return result;
}

export const SQ_COLORS: Record<SqColorName, SqColorModes> = buildSqColors();

export function sqColorPair(
	name: SqColorName,
	mode: SqColorMode,
): SqColorGradientPair {
	return SQ_COLORS[name][mode];
}

export function sqColorStop(
	name: SqColorName,
	mode: SqColorMode,
	stop: "from" | "to",
): string {
	return SQ_COLORS[name][mode][stop];
}

export function sqColorStripeRgba(
	name: SqColorName,
	mode: SqColorMode = "static",
	opacity = 0.3,
): string {
	return hexToRgba(SQ_COLORS[name][mode].from, opacity);
}

export function sqColorRgba(hex: string, opacity: number): string {
	return hexToRgba(hex, opacity);
}

/** All registered color names (for CSS var generation / tests). */
export function sqColorNames(): readonly SqColorName[] {
	return SQ_COLOR_NAMES;
}
